/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { SectionGrid, Flex } from "../../components/Layout"
import { Logo } from "frontend/src/components/Logo"
import { useState } from "react"
import Img from "gatsby-image"
import { getFluidHeightFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import Carousel from "nuka-carousel"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"

// styles
const buttonStyles = {
  cursor: "pointer",
  background: "#FFFFFF",
  border: "none",
  width: "55px",
  height: "55px",
  py: 4,
  px: 4,
  borderRadius: "50%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  "&:active": {
    outline: "none",
  },
  "&:focus": {
    outline: "none",
  },
}

// subcomponents
function PressSliderTitle({ title, fontColor }) {
  return (
    <h3
      sx={{
        fontFamily: "heading",
        color: fontColor,
        fontSize: ["28px", "28px", "38px"],
        lineHeight: ["30px", "30px", "44px"],
        letterSpacing: "-1.24px",
        fontWeight: ["medium", "medium", "regular"],
        mx: "auto",
        mb: 8,
        pt: 0,
        textAlign: "center",
        width: ["84%", "74%", "auto"],
      }}
    >
      {title}
    </h3>
  )
}

function PressSliderArticleDesktop({ arrayOfArticles, fontColor }) {
  const [slideIndex, setSlideIndex] = useState(0)
  // console.log(slideIndex)
  // Slide event handler
  const handleSlideToShow = index => {
    setSlideIndex(index)
  }
  const numberOfArticles = arrayOfArticles.length
  return (
    <div
      sx={{
        display: ["none", "block"],
        width: "100%",
        height: "100%",
        "&.active": {
          border: "none",
          outline: "none",
        },
        "&.focus": {
          border: "none",
          outline: "none",
        },
        "*:focus": {
          outline: "none",
        },
      }}
    >
      <Carousel
        slideIndex={slideIndex}
        slidesToShow={1}
        enableKeyboardControls={true}
        dragging={true}
        wrapAround={true}
        afterSlide={index => setSlideIndex(index)}
        renderCenterLeftControls={() => (
          <PrevSlideSelector
            changeSlide={handleSlideToShow}
            numberOfArticles={numberOfArticles}
            current={slideIndex}
          />
        )}
        renderBottomCenterControls={() => null}
        renderCenterRightControls={() => (
          <NextSlideSelector
            changeSlide={handleSlideToShow}
            numberOfArticles={numberOfArticles}
            current={slideIndex}
          />
        )}
        renderBottomLeftControls={() => null}
        frameOverflow="visible"
        animation="zoom"
        cellAlign="center"
        zoomScale={1}
        width="100%"
        slideWidth={1}
        slideOffset={100}
      >
        {arrayOfArticles.map((item, i) => {
          return (
            <ArticlePressSlider
              article={item}
              key={i}
              currentSlide={slideIndex}
              index={i}
              fontColor={fontColor}
            />
          )
        })}
      </Carousel>
    </div>
  )
}

function PressSliderArticleMobile({ arrayOfArticles, fontColor }) {
  const [slideIndex, setSlideIndex] = useState(0)
  // console.log(slideIndex)
  // Slide event handler
  const handleSlideToShow = index => {
    setSlideIndex(index)
  }
  const numberOfArticles = arrayOfArticles.length
  return (
    <div
      sx={{
        display: ["block", "none"],
        width: "100%",
        height: "100%",
        "&.active": {
          border: "none",
          outline: "none",
        },
        "&.focus": {
          border: "none",
          outline: "none",
        },
        "*:focus": {
          outline: "none",
        },
      }}
    >
      <Carousel
        slideIndex={slideIndex}
        slidesToShow={1}
        enableKeyboardControls={true}
        dragging={true}
        wrapAround={true}
        afterSlide={index => setSlideIndex(index)}
        renderCenterLeftControls={() => (
          <PrevSlideSelector
            changeSlide={handleSlideToShow}
            numberOfArticles={numberOfArticles}
            current={slideIndex}
          />
        )}
        renderBottomCenterControls={() => null}
        renderCenterRightControls={() => (
          <NextSlideSelector
            changeSlide={handleSlideToShow}
            numberOfArticles={numberOfArticles}
            current={slideIndex}
          />
        )}
        renderBottomLeftControls={() => null}
        frameOverflow="visible"
        width="100%"
        slideWidth={1}
      >
        {arrayOfArticles.map((item, i) => {
          return (
            <ArticlePressSlider
              article={item}
              key={i}
              currentSlide={slideIndex}
              index={i}
              fontColor={fontColor}
            />
          )
        })}
      </Carousel>
    </div>
  )
}

function ArticlePressSlider({ article, currentSlide, index, fontColor }) {
  const { articleTitle: title, sourceLogo, sourceImage } = article
  const filterFunction =
    index === currentSlide ? "unset" : "opacity(0.3) grayscale(0.2) brightness(1)"
  return (
    <Flex.column
      justify="space-between"
      align="center"
      sx={{
        width: ["90vw", "85%"],
        maxHeight: "230px",
        minHeight: ["unset", "230px"],
        px: ["38px", 11],
        py: [8, 13],
        mx: "auto",
        textAlign: "center",
        filter: filterFunction,
      }}
    >
      <h4
        sx={{
          fontFamily: "heading",
          fontSize: [0, 4, "18px"],
          fontWeight: "light",
          lineHeight: [1.3, 1.3, 1.4],
          letterSpacing: "0px",
          marginBottom: ["16px", "15px", "18px"],
          color: fontColor,
        }}
      >
        {title}
      </h4>
      {sourceImage ? (
        <Img
          fluid={getFluidHeightFromAsset(sourceImage, 200)}
          style={{ maxHeight: "30px", maxWidth: "80%", minWidth: "50%", mt: 8, mr: 0 }}
          imgStyle={{
            maxHeight: "100%",
            maxWidth: "100%",
            objectFit: "contain",
          }}
        />
      ) : sourceLogo ? (
        <Logo
          name={sourceLogo}
          height={["30px"]}
          sx={{ maxHeight: "30px", maxWidth: "80%", mt: 8, mr: 0 }}
        />
      ) : (
        ""
      )}
    </Flex.column>
  )
}

function PrevSlideSelector({ changeSlide, numberOfArticles, current }) {
  const prev = current === 0 ? numberOfArticles - 1 : current - 1
  return (
    <button
      sx={{ ...buttonStyles, boxShadow: "rgba(27, 45, 69, 0.1) 0px 5px 10px 0px", ml: [0, 8, 6] }}
      onClick={() => changeSlide(prev)}
    >
      <FiChevronLeft sx={{ fontSize: "25px" }} />
    </button>
  )
}

function NextSlideSelector({ changeSlide, numberOfArticles, current }) {
  const next = current === numberOfArticles - 1 ? 0 : current + 1
  return (
    <button
      sx={{ ...buttonStyles, boxShadow: "rgba(27, 45, 69, 0.1) 0px 5px 10px 0px", mr: [0, 8, 6] }}
      onClick={() => changeSlide(next)}
    >
      <FiChevronRight sx={{ fontSize: "25px" }} />
    </button>
  )
}

// main component
function PressSlider({ title, allArticles, backgroundColor, fontColor }) {
  return (
    <SectionGrid
      sx={{
        overflow: "hidden",
        position: "relative",
        width: "100%",
        backgroundColor: backgroundColor,
        px: ["2%", "3%"],
        pt: [9, 7, 10],
        pb: [11, 12, 10],
      }}
    >
      <SectionGrid.narrow sx={{ backgroundColor: backgroundColor }}>
        <PressSliderTitle title={title} fontColor={fontColor} />
        <div>
          <PressSliderArticleDesktop arrayOfArticles={allArticles} fontColor={fontColor} />
          <PressSliderArticleMobile arrayOfArticles={allArticles} fontColor={fontColor} />
        </div>
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { PressSlider }
